/**
 * OTO-22: pure audio-anchored transport clock math.
 *
 * Two-clock pattern -- the audio clock (`Tone.context.currentTime`) is the
 * time authority; the visual/paint clock (rAF) is a read-only derivation of
 * it. An "anchor" pins one (audioTime, transportTime, tempoMultiplier)
 * triple; any later transport position is a pure linear derivation from that
 * pin, so a main-thread stall (rAF pause) can never desync audio scheduling
 * from the transport -- the audio clock never stops advancing.
 *
 * No Tone import here: `audioNow` is always passed in by the caller so this
 * module stays pure and deterministic for tests (see `utils/audioNow.ts` for
 * the real `Tone.context.currentTime` source).
 */
export interface PlaybackAnchor {
  /** Audio-clock time (seconds) at which this anchor was created. */
  audioTime: number;
  /** Transport position (seconds) at the anchor instant. */
  transportTime: number;
  /** Playback speed multiplier in effect from the anchor instant onward. */
  tempoMultiplier: number;
}

function assertFinite(value: number, label: string, fn: string): void {
  if (!Number.isFinite(value)) {
    throw new Error(`[playbackClock/${fn}] Failed: ${label} must be finite | ${label}=${value}`);
  }
}

/**
 * Constructs a new anchor. This is the single pure constructor used by the
 * store's re-anchor funnel (`playbackStore.ts`) -- every transport lifecycle
 * event creates a fresh anchor through here, never by hand.
 *
 * Fail-fast: non-finite inputs and non-positive tempo throw with context
 * (invalid input, not a legitimate paused/frozen state -- see the
 * null-anchor carve-out documented on the store and scheduler).
 */
export function createAnchor(params: {
  audioNow: number;
  transportTime: number;
  tempoMultiplier: number;
}): PlaybackAnchor {
  const { audioNow, transportTime, tempoMultiplier } = params;
  assertFinite(audioNow, 'audioNow', 'createAnchor');
  assertFinite(transportTime, 'transportTime', 'createAnchor');
  assertFinite(tempoMultiplier, 'tempoMultiplier', 'createAnchor');
  if (tempoMultiplier <= 0) {
    throw new Error(
      `[playbackClock/createAnchor] Failed: tempoMultiplier must be > 0 | tempoMultiplier=${tempoMultiplier}`
    );
  }
  return { audioTime: audioNow, transportTime, tempoMultiplier };
}

/**
 * Derives the transport position at a given audio-clock instant. Pure read;
 * never mutates the anchor. A scheduler/engine tick with `anchor === null`
 * is a normal frozen-transport no-op (pause/stop/waiting/hidden) -- callers
 * must check for null themselves, not call this function with one.
 */
export function transportTimeAt(anchor: PlaybackAnchor, audioNow: number): number {
  assertFinite(audioNow, 'audioNow', 'transportTimeAt');
  return anchor.transportTime + (audioNow - anchor.audioTime) * anchor.tempoMultiplier;
}

/**
 * Inverse of `transportTimeAt`: the audio-clock instant at which a given
 * transport time occurs under this anchor. Used by the lookahead scheduler
 * to convert a chord's `startTime` (transport seconds) into the explicit
 * audio-clock timestamp passed to `triggerAttack` -- never `Tone.now()`.
 */
export function audioTimeFor(anchor: PlaybackAnchor, transportTime: number): number {
  assertFinite(transportTime, 'transportTime', 'audioTimeFor');
  return anchor.audioTime + (transportTime - anchor.transportTime) / anchor.tempoMultiplier;
}

/** Result of `applyLoopWrap`. */
export interface LoopWrapResult {
  /** The (possibly wrapped) transport time. */
  time: number;
  /** True if `time` crossed `loopEnd` and was wrapped back into the region. */
  wrapped: boolean;
}

/**
 * Wraps a transport time into `[loopStart, loopEnd)` once it reaches or
 * passes `loopEnd`. Handles multi-wrap (time landing several loop-lengths
 * past `loopEnd`, e.g. after a long main-thread stall) via modulo rather
 * than a single subtraction. Passes through unchanged when no loop region
 * is active (`loopStart`/`loopEnd` null) or time hasn't reached `loopEnd`.
 *
 * Fail-fast: non-finite inputs and `loopEnd <= loopStart` throw with
 * context -- a caller-side invariant violation, not a normal state.
 */
export function applyLoopWrap(
  time: number,
  loopStart: number | null,
  loopEnd: number | null
): LoopWrapResult {
  if (loopStart === null || loopEnd === null) {
    return { time, wrapped: false };
  }
  assertFinite(time, 'time', 'applyLoopWrap');
  assertFinite(loopStart, 'loopStart', 'applyLoopWrap');
  assertFinite(loopEnd, 'loopEnd', 'applyLoopWrap');
  if (loopEnd <= loopStart) {
    throw new Error(
      `[playbackClock/applyLoopWrap] Failed: loopEnd must be > loopStart | loopStart=${loopStart} loopEnd=${loopEnd}`
    );
  }
  if (time < loopEnd) {
    return { time, wrapped: false };
  }
  const loopLength = loopEnd - loopStart;
  const wrappedOffset = (time - loopStart) % loopLength;
  return { time: loopStart + wrappedOffset, wrapped: true };
}
